import { Grid } from '@mui/material';
import './OrderBox.scss';
import React from 'react';
import { useDndContext } from '@dnd-kit/core';
import { AccountData, OrderData, SchedulerConfigurationData, UnavailabilityData } from '../../api';
import OrderBox from './OrderBox';
import UnavailabilityBox from './UnavailabilityBox';
import { useAuth } from '../../session/InternalAuthProvider';
import { DEFAULT_START_OFFSET } from './CalendarCalculator';
import { GeneratedResource, ResourceColumn, ResourceDropState } from './ResourceColumn';
import { getAvailableResourcesForOrder } from '../views/dragndrop/resourceValidationUtils';
import { useLanguage } from '../../common/GeneralUtils';
import { getOrderPage } from '../../utils/AccountUtils';
import { isManuallySeated, pageConfigOf, seatedIndexesInConfig, seatedTableCount } from '../utils/orderSeating';
import { createResourceId, UNASSIGNED_ID } from '../views/dragndrop/DragDropTypes';
import { DEFAULT_ORDER_PAGE_COLOR } from '../../common/theme/mbTokens';

type ResourcesBoxProps = {
  orders: OrderData[];
  unavailabilities: UnavailabilityData[];
  css?: any;
  openOrder: (event: any, order: OrderData) => void;
};

export const ResourcesBox = ({ orders, unavailabilities, css, openOrder }: ResourcesBoxProps) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { active } = useDndContext();

  // Get the currently dragged order for validation
  const draggedOrder = active ? orders.find((order) => order.id === active.id) : null;

  // Generate resources from scheduler configs
  const generateResources = (): GeneratedResource[] => {
    const resources: GeneratedResource[] = [];
    const schedulerConfigs = account?.shared_resources?.scheduler_configs || [];

    schedulerConfigs.forEach((config: SchedulerConfigurationData) => {
      // Get workshop color from the first allowed mask (order page)
      const primaryOrderPageId = config.allowed_masks?.[0]?.order_page_id;
      const orderPage = primaryOrderPageId ? getOrderPage(account, primaryOrderPageId) : null;
      const workshopColor = orderPage?.color || DEFAULT_ORDER_PAGE_COLOR;

      for (let i = 1; i <= config.resource_count; i++) {
        const resourceLabel = config.resource_labels?.[i - 1];
        const labelName =
          resourceLabel?.[language as keyof typeof resourceLabel] ||
          resourceLabel?.en ||
          `${(config.label as any)[language] || (config.label as any).en || 'Resource'} ${i}`;

        resources.push({
          id: createResourceId(config.id, i),
          name: labelName,
          index: i,
          schedulerConfigId: config.id,
          workshopColor,
        });
      }
    });

    return resources;
  };

  const resources = generateResources();
  const totalColumns = resources.length + 1; // +1 for unassigned column
  const columnWidth = 100 / totalColumns;

  // Calculate drop states for resources based on dragged order
  const getResourceDropState = (resource: GeneratedResource): ResourceDropState => {
    if (!draggedOrder) {
      return ResourceDropState.NEUTRAL;
    }

    // Get available resources for the dragged order
    const availableResources = getAvailableResourcesForOrder(draggedOrder, account);

    if (!availableResources || availableResources.schedulerConfigId !== resource.schedulerConfigId) {
      // Wrong scheduler config group
      return ResourceDropState.INVALID;
    }

    if (!availableResources.availableResourceIds.includes(resource.index - 1)) {
      // Mask doesn't allow this resource
      return ResourceDropState.UNAVAILABLE;
    }

    // Check if there's enough space from this position
    const resourcesNeeded = Math.ceil(
      (draggedOrder.persons || 1) /
        (account?.shared_resources?.scheduler_configs?.find((c) => c.id === resource.schedulerConfigId)
          ?.persons_per_resource || 1),
    );

    const config = account?.shared_resources?.scheduler_configs?.find((c) => c.id === resource.schedulerConfigId);
    if (config && resource.index - 1 + resourcesNeeded > config.resource_count) {
      // Not enough space
      return ResourceDropState.INVALID;
    }

    return ResourceDropState.VALID;
  };

  // Calculate how many resources the dragged order would need
  const getResourceSpanForOrder = (resource: GeneratedResource): number => {
    if (!draggedOrder) return 1;

    const config = account?.shared_resources?.scheduler_configs?.find((c) => c.id === resource.schedulerConfigId);
    if (!config) return 1;

    return Math.ceil((draggedOrder.persons || 1) / config.persons_per_resource);
  };

  // Utility functions for collision detection (adapted from CalendarCalculator.ts)
  const getOrderStartTime = (order: OrderData) => {
    return order.time || 0;
  };

  const getOrderEndTime = (order: OrderData) => {
    return (order.time || 0) + (order.duration || 0);
  };

  const ordersOverlap = (orderA: OrderData, orderB: OrderData): boolean => {
    return (
      getOrderStartTime(orderA) < getOrderEndTime(orderB) && // Order A's start time is before Order B's end time
      getOrderEndTime(orderA) > getOrderStartTime(orderB) // Order A's end time is after Order B's start time
    );
  };

  // Group overlapping orders for collision detection
  const groupOverlappingOrders = (ordersToGroup: OrderData[]): OrderData[][] => {
    // Sort orders by start time
    const sortedOrders = [...ordersToGroup].sort((a, b) => getOrderStartTime(a) - getOrderStartTime(b));

    // Initialize groups
    const orderGroups: OrderData[][] = [];

    // Group overlapping orders
    sortedOrders.forEach((order) => {
      let added = false;
      for (const group of orderGroups) {
        // Check if the order overlaps with any order in the group
        if (group.some((groupOrder) => ordersOverlap(order, groupOrder))) {
          group.push(order);
          added = true;
          break;
        }
      }
      if (!added) {
        orderGroups.push([order]);
      }
    });

    return orderGroups;
  };

  // Calculate time-based CSS for orders
  const calculateOrderCss = (
    order: OrderData,
    resourceIndex: number,
    resourceSpan = 1,
    collisionIndex = 0,
    totalCollisions = 1,
  ) => {
    const HOUR_HEIGHT = 50;
    let top = HOUR_HEIGHT * (order.time! / 3600 - DEFAULT_START_OFFSET);
    if (top < 0) {
      top = 0;
    }

    const height = (order.duration / 3600) * HOUR_HEIGHT;

    // Calculate width and left position based on collisions
    const baseColumnWidth = columnWidth * resourceSpan;
    const collisionWidth = baseColumnWidth / totalCollisions;
    const width = collisionWidth - 0.2; // Subtract padding
    const baseLeft = resourceIndex * columnWidth;
    const left = baseLeft + collisionIndex * collisionWidth;

    return { top, height, width, left };
  };

  // Get individual unavailability boxes for each blocked resource position
  const getIndividualUnavailabilityBlocks = (): Array<UnavailabilityData & { css: any; resourceId: string }> => {
    const individualBlocks: Array<UnavailabilityData & { css: any; resourceId: string }> = [];

    unavailabilities.forEach((unavailability) => {
      const schedulerBlocks = unavailability.shared_resources_blocks?.scheduler_configs;
      if (!schedulerBlocks || schedulerBlocks.length === 0) {
        return; // Skip unavailabilities without scheduler blocks
      }

      // Create individual blocks for each blocked resource position
      schedulerBlocks.forEach((block) => {
        if (block.blocked_mask) {
          block.blocked_mask.split('').forEach((bit, index) => {
            if (bit === '1') {
              // Find the scheduler config to get resource count for position reversal
              const config = account?.shared_resources?.scheduler_configs?.find(
                (c) => c.id === block.scheduler_config_id,
              );
              const resourceCount = config?.resource_count || 0;
              const reversedIndex = resourceCount - 1 - index;

              // Find the global resource index for this position
              const globalIndex = resources.findIndex(
                (r) => r.schedulerConfigId === block.scheduler_config_id && r.index - 1 === reversedIndex,
              );

              if (globalIndex >= 0) {
                const css = calculateOrderCss(
                  {
                    time: unavailability.start_time,
                    duration: unavailability.end_time - unavailability.start_time,
                    id: `${unavailability.id}-${globalIndex}`, // Unique ID for each block
                  } as OrderData,
                  globalIndex,
                  1, // Single resource span
                );

                individualBlocks.push({
                  ...unavailability,
                  css,
                  resourceId: resources[globalIndex].id,
                });
              }
            }
          });
        }
      });
    });

    return individualBlocks;
  };

  // Get orders for a specific resource with CSS
  const getOrdersForResource = (
    resource: GeneratedResource,
  ): Array<OrderData & { css: any; schedulerConfigId?: string }> => {
    const resourceConfig = (account?.shared_resources?.scheduler_configs || []).find(
      (config) => config.id === resource.schedulerConfigId,
    );
    if (!resourceConfig) {
      return [];
    }
    const resourceOrders = orders.filter((order) =>
      seatedIndexesInConfig(order, resourceConfig).includes(resource.index - 1),
    );

    return resourceOrders.map((order) => {
      const resourceIndexes = seatedIndexesInConfig(order, resourceConfig);
      const minIndex = Math.min(...resourceIndexes);
      const resourceSpan = resourceIndexes.length;
      const globalResourceIndex = resources.findIndex(
        (r) => r.index - 1 === minIndex && r.schedulerConfigId === resource.schedulerConfigId,
      );

      return {
        ...order,
        schedulerConfigId: resource.schedulerConfigId,
        css: calculateOrderCss(order, globalResourceIndex, resourceSpan),
      };
    });
  };

  // Get unassigned orders with CSS (with collision detection)
  const getUnassignedOrders = (): Array<OrderData & { css: any }> => {
    const unassignedOrders = orders.filter(
      (order) =>
        seatedTableCount(order) === 0 || (!isManuallySeated(order) && pageConfigOf(order, account) === undefined),
    );

    // Group overlapping orders for collision detection
    const orderGroups = groupOverlappingOrders(unassignedOrders);

    // Flatten the groups with collision information
    const ordersWithCollisionInfo: Array<OrderData & { css: any }> = [];

    orderGroups.forEach((group) => {
      group.forEach((order, collisionIndex) => {
        const css = calculateOrderCss(
          order,
          resources.length, // Last column (unassigned)
          1, // Resource span
          collisionIndex,
          group.length, // Total collisions in this group
        );
        ordersWithCollisionInfo.push({
          ...order,
          css,
        });
      });
    });

    return ordersWithCollisionInfo;
  };

  return (
    <Grid
      sx={{
        width: `calc(${css?.width || 100}%)`,
        insetInlineStart: `${css?.left || 0}%`,
        marginTop: `${css?.top || 0}px`,
        height: `${css?.height || 50 * 17}px`, // Add explicit height
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
      }}>
      {/* Resource Columns */}
      <Grid container sx={{ flex: 1, position: 'relative', height: '100%' }}>
        {resources.map((resource, index) => (
          <ResourceColumn
            key={resource.id}
            resource={resource}
            orders={getOrdersForResource(resource)}
            columnWidth={columnWidth}
            leftPosition={index * columnWidth}
            openOrder={openOrder}
            dropState={getResourceDropState(resource)}
            previewSpan={getResourceSpanForOrder(resource)}
          />
        ))}

        {/* Unassigned Column */}
        <ResourceColumn
          key={UNASSIGNED_ID}
          resource={null}
          orders={getUnassignedOrders()}
          columnWidth={columnWidth}
          leftPosition={resources.length * columnWidth}
          openOrder={openOrder}
        />

        {/* Render individual unavailability blocks for each blocked resource position (behind orders) */}
        {getIndividualUnavailabilityBlocks().map((unavailabilityBlock, index) => (
          <UnavailabilityBox
            key={`${unavailabilityBlock.id}-${unavailabilityBlock.resourceId}-${index}`}
            unavailability={unavailabilityBlock}
            viewType="day"
            css={unavailabilityBlock.css}
          />
        ))}

        {/* Render all orders with proper positioning (on top of unavailabilities) */}
        {resources
          .flatMap((resource) => getOrdersForResource(resource))
          .concat(getUnassignedOrders())
          .filter(
            (order, index, self) =>
              self.findIndex((o) => o.id === order.id && o.schedulerConfigId === order.schedulerConfigId) === index,
          )
          .map((orderWithCss) => (
            <OrderBox
              key={`${orderWithCss.id}-${orderWithCss.schedulerConfigId}`}
              order={orderWithCss}
              viewType="day"
              css={orderWithCss.css}
              openOrder={(e) => openOrder(e, orderWithCss)}
              hideEmployees
            />
          ))}
      </Grid>
    </Grid>
  );
};
